import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const DynamicSectionRenderer = ({ sections }) => {
  const { language } = useLanguage();

  if (!sections || sections.length === 0) return null;

  const renderCards = (section) => (
    <div key={section.id} className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            {language === 'en' ? section.title_en : section.title_hi}
          </h2>
          {section.subtitle_en && (
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'en' ? section.subtitle_en : section.subtitle_hi}
            </p>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {section.items?.map((item, index) => (
            <div
              key={index}
              className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
            >
              {item.image_url && (
                <img
                  src={item.image_url}
                  alt={language === 'en' ? item.title_en : item.title_hi}
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
              )}
              <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                {language === 'en' ? item.title_en : item.title_hi}
              </h3>
              <p className="text-muted-foreground">
                {language === 'en' ? item.description_en : item.description_hi}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderList = (section) => (
    <div key={section.id} className="py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            {language === 'en' ? section.title_en : section.title_hi}
          </h2>
          {section.subtitle_en && (
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'en' ? section.subtitle_en : section.subtitle_hi}
            </p>
          )}
        </div>
        <div className="space-y-6">
          {section.items?.map((item, index) => (
            <div
              key={index}
              className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm flex gap-6 items-start"
            >
              {item.image_url && (
                <img
                  src={item.image_url}
                  alt={language === 'en' ? item.title_en : item.title_hi}
                  className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                />
              )}
              <div>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                  {language === 'en' ? item.title_en : item.title_hi}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'en' ? item.description_en : item.description_hi}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderBanner = (section) => (
    <div
      key={section.id}
      className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-primary to-primary/80 text-white"
    >
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
          {language === 'en' ? section.title_en : section.title_hi}
        </h2>
        {section.subtitle_en && (
          <p className="text-xl opacity-90">
            {language === 'en' ? section.subtitle_en : section.subtitle_hi}
          </p>
        )}
      </div>
    </div>
  );

  const renderText = (section) => (
    <div key={section.id} className="py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            {language === 'en' ? section.title_en : section.title_hi}
          </h2>
        </div>
        {section.subtitle_en && (
          <div className="prose prose-lg max-w-none text-muted-foreground">
            <p>{language === 'en' ? section.subtitle_en : section.subtitle_hi}</p>
          </div>
        )}
        {section.items?.map((item, index) => (
          <div key={index} className="mt-6">
            {item.title_en && (
              <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                {language === 'en' ? item.title_en : item.title_hi}
              </h3>
            )}
            {item.description_en && (
              <p className="text-muted-foreground">
                {language === 'en' ? item.description_en : item.description_hi}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {sections.map((section) => {
        switch (section.section_type) {
          case 'cards':
            return renderCards(section);
          case 'list':
            return renderList(section);
          case 'banner':
            return renderBanner(section);
          case 'text':
            return renderText(section);
          default:
            return renderCards(section);
        }
      })}
    </>
  );
};

export default DynamicSectionRenderer;
